<script setup>
import { computed, onMounted, onUnmounted, ref } from 'vue'
import { RouterLink, useRoute, useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useSettingsStore } from '@/stores/settings'
import { useNotificationStore } from '@/stores/notifications'
import { useTutorialStore } from '@/stores/tutorial'
import { useLookup } from '@/composables/searchLookup'

import SingleSearchSelect from '@/components/form_builders/SingleSearchSelect.vue'
import Notifications from '@/components/common/Notifications.vue'
import AccessibilityWidget from '@/components/common/AccessibilityWidget.vue'
import familyLogo from '@/assets/family_crest.webp'
import branchesIcon from '@/assets/icons/branches.svg?url'
import reunionsIcon from '@/assets/icons/reunions.svg?url'
import heirloomsIcon from '@/assets/icons/heirlooms.svg?url'
import propertiesIcon from '@/assets/icons/properties.svg?url'
import storeIcon from '@/assets/icons/store.svg?url'
import directoryIcon from '@/assets/icons/directory.svg?url'
import adminIcon from '@/assets/icons/admin.svg?url'
import profileIcon from '@/assets/icons/profile.svg?url'
import settingsIcon from '@/assets/icons/settings.svg?url'
import logoutIcon from '@/assets/icons/logout.svg?url'
import recipeIcon from '@/assets/icons/recipes.svg?url'
import tutorialIcon from '@/assets/icons/tutorial.svg?url'

const router = useRouter()
const route = useRoute()
const auth = useAuthStore()
const settings = useSettingsStore()
const notifications = useNotificationStore()
const tutorial = useTutorialStore()
const connections = useLookup('connections')

const accountMenuOpen = ref(false)
const peopleSearch = ref(null)

const avatar = computed(() => {
  return auth.avatar
})

const activeTabColor = computed(() => {
  return settings.activeTabColor
})

const canViewAdmin = computed(() => {
  return Boolean(
    auth.canViewSiteAdmin ||
    auth.canManageSiteAdmin ||
    auth.canManageBranchAdmin ||
    auth.canReviewChanges
  )
})

const adminNotificationCount = computed(() => {
  return notifications.items
    .filter(item => {
      return (
        item.type === 'bucket' &&
        Number(item.bucket_code) === 1
      )
    })
    .reduce((total, item) => {
      return total + Number(item.count || 0)
    }, 0)
})

function toggleAccountMenu() {
  accountMenuOpen.value = !accountMenuOpen.value
}

function closeAccountMenu() {
  accountMenuOpen.value = false
}

function navigateFromAccountMenu(path) {
  void router.push(path).finally(() => {
    closeAccountMenu()
  })
}

function startCurrentPageTutorial() {
  closeAccountMenu()
  tutorial.startForPage({ path: route.path })
}

function handleKeydown(event) {
  if (event.key === 'Escape') {
    closeAccountMenu()
  }
}

function isNavActive(path) {
  return (
    route.path === path ||
    route.path.startsWith(`${path}/`)
  )
}

function iconStyle(icon, active = false) {
  return {
    backgroundColor: active
      ? 'var(--nav-accent)'
      : 'currentColor',
    WebkitMaskImage: `url("${icon}")`,
    WebkitMaskRepeat: 'no-repeat',
    WebkitMaskPosition: 'center',
    WebkitMaskSize: 'contain',
    maskImage: `url("${icon}")`,
    maskRepeat: 'no-repeat',
    maskPosition: 'center',
    maskSize: 'contain'
  }
}

function searchPeopleByName(
  selectedPerson = peopleSearch.value
) {
  const userDetailId =
    selectedPerson?.user_detail_id

  if (!userDetailId) return

  closeAccountMenu()

  router.push({
    path: `/family_members/${userDetailId}`
  })

  peopleSearch.value = null
}

async function handleLogout() {
  try {
    await auth.logout()
  } finally {
    closeAccountMenu()
    router.push('/')
  }
}

onMounted(() => {
  window.addEventListener(
    'keydown',
    handleKeydown
  )
})

onUnmounted(() => {
  window.removeEventListener(
    'keydown',
    handleKeydown
  )
})
</script>

<template>
  <header
    class="app-header"
    :style="{
      '--nav-accent': activeTabColor
    }"
  >
    <RouterLink
      to="/home"
      class="app-header__brand"
      data-tutorial="home"
      aria-label="Go to home"
      @click="closeAccountMenu"
    >
      <img
        :src="familyLogo"
        alt="Troubetzkoy logo"
        class="logo"
      />
    </RouterLink>

    <form
      class="app-header__search"
      data-tutorial="search"
      role="search"
      @submit.prevent="searchPeopleByName()"
    >
      <SingleSearchSelect
        v-model="peopleSearch"
        labelName=""
        ariaLabel="Search family members"
        placeholder="Search family members"
        fieldClass="app-header__search-field"
        :lookupOptions="connections"
        :optionLabel="o => `${o.label_name}`"
        :returnFormat="o => ({
          user_detail_id: o.user_detail_id,
          label_name: o.label_name
        })"
        @update:modelValue="searchPeopleByName"
      />
    </form>

    <div class="app-header__actions">
      <div class="notifications-slot" data-tutorial="notifications">
        <Notifications />
      </div>

      <div class="accessibility-slot" data-tutorial="accessibility">
        <AccessibilityWidget />
      </div>

      <div class="tutorial-slot">
        <button
          type="button"
          class="app-header__action"
          data-tutorial="restart"
          aria-label="Start tutorial for this page"
          title="Page tutorial"
          @click="startCurrentPageTutorial"
        >
          <span
            class="app-header__action-icon"
            :style="iconStyle(tutorialIcon)"
            aria-hidden="true"
          />
        </button>
      </div>

      <div class="nav-menu app-header__account" data-tutorial="account">
        <button
          type="button"
          class="app-header__avatar-trigger app-header__action app-header__action--avatar"
          :style="{
            '--profile-ring-color': activeTabColor,
            borderColor: activeTabColor
          }"
          :aria-expanded="accountMenuOpen"
          aria-haspopup="true"
          aria-controls="account-menu"
          aria-label="Open account menu"
          @click="toggleAccountMenu"
        >
          <img
            v-if="avatar?.url"
            :src="avatar.url"
            alt="User avatar"
            class="app-header__avatar"
          />

          <span
            v-else
            class="app-header__avatar-placeholder"
            :style="iconStyle(profileIcon)"
            aria-hidden="true"
          />
        </button>

        <div
          v-if="accountMenuOpen"
          id="account-menu"
          class="nav-menu-panel"
        >
          <RouterLink
            :to="`/family_members/${auth.user_detail_id}`"
            custom
            v-slot="{ href }"
          >
            <a
              :href="href"
              class="nav-menu-item"
              @click.prevent="navigateFromAccountMenu(
                `/family_members/${auth.user_detail_id}`
              )"
            >
              <span
                class="nav-menu-icon"
                :style="iconStyle(profileIcon)"
                aria-hidden="true"
              />

              <span>Profile</span>
            </a>
          </RouterLink>

          <RouterLink
            :to="`/family_members/${auth.user_detail_id}/settings`"
            custom
            v-slot="{ href }"
          >
            <a
              :href="href"
              class="nav-menu-item"
              @click.prevent="navigateFromAccountMenu(
                `/family_members/${auth.user_detail_id}/settings`
              )"
            >
              <span
                class="nav-menu-icon"
                :style="iconStyle(settingsIcon)"
                aria-hidden="true"
              />

              <span>Settings</span>
            </a>
          </RouterLink>

          <button
            type="button"
            class="nav-menu-item nav-menu-item--button"
            @click="handleLogout"
          >
            <span
              class="nav-menu-icon"
              :style="iconStyle(logoutIcon)"
              aria-hidden="true"
            />

            <span>Logout</span>
          </button>
        </div>
      </div>
    </div>

    <nav
      class="app-nav"
      data-tutorial="navigation"
      aria-label="Main navigation"
    >
      <div class="nav-links">
        <RouterLink
          to="/branches"
          class="nav-link"
          data-tutorial="branches"
          @click="closeAccountMenu"
        >
          <span
            class="nav-link-icon"
            :style="iconStyle(
              branchesIcon,
              isNavActive('/branches')
            )"
            aria-hidden="true"
          />

          <span>Branches</span>
        </RouterLink>

        <RouterLink
          to="/reunions"
          class="nav-link"
          data-tutorial="reunions"
          @click="closeAccountMenu"
        >
          <span
            class="nav-link-icon"
            :style="iconStyle(
              reunionsIcon,
              isNavActive('/reunions')
            )"
            aria-hidden="true"
          />

          <span>Reunions</span>
        </RouterLink>

        <RouterLink
          to="/heirlooms"
          class="nav-link"
          data-tutorial="heirlooms"
          @click="closeAccountMenu"
        >
          <span
            class="nav-link-icon"
            :style="iconStyle(
              heirloomsIcon,
              isNavActive('/heirlooms')
            )"
            aria-hidden="true"
          />

          <span>Heirlooms</span>
        </RouterLink>

        <RouterLink
          to="/properties"
          class="nav-link"
          data-tutorial="properties"
          @click="closeAccountMenu"
        >
          <span
            class="nav-link-icon"
            :style="iconStyle(
              propertiesIcon,
              isNavActive('/properties')
            )"
            aria-hidden="true"
          />

          <span>Properties</span>
        </RouterLink>

        <RouterLink
          to="/recipes"
          class="nav-link"
          data-tutorial="recipes"
          @click="closeAccountMenu"
        >
          <span
            class="nav-link-icon"
            :style="iconStyle(
              recipeIcon,
              isNavActive('/recipes')
            )"
            aria-hidden="true"
          />

          <span>Recipes</span>
        </RouterLink>

        <RouterLink
          to="/store"
          class="nav-link"
          data-tutorial="store"
          @click="closeAccountMenu"
        >
          <span
            class="nav-link-icon"
            :style="iconStyle(
              storeIcon,
              isNavActive('/store')
            )"
            aria-hidden="true"
          />

          <span>Store (Demo)</span>
        </RouterLink>

        <RouterLink
          to="/family_members"
          class="nav-link"
          data-tutorial="directory"
          @click="closeAccountMenu"
        >
          <span
            class="nav-link-icon"
            :style="iconStyle(
              directoryIcon,
              isNavActive('/family_members')
            )"
            aria-hidden="true"
          />

          <span>Directory</span>
        </RouterLink>

        <RouterLink
          v-if="canViewAdmin"
          to="/site_admin"
          class="nav-link"
          data-tutorial="admin"
          @click="closeAccountMenu"
        >
          <span
            class="nav-link-icon"
            :style="iconStyle(
              adminIcon,
              isNavActive('/site_admin')
            )"
            aria-hidden="true"
          />

          <span>Admin</span>

          <span
            v-if="adminNotificationCount > 0"
            class="count badge badge--danger"
          >
            {{ adminNotificationCount }}
          </span>
        </RouterLink>
      </div>
    </nav>
  </header>
</template>
